import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Alert, Button, Empty, Flex, Form, Input, List, Modal, Select, Spin, Typography, message } from "antd";
import dayjs from "dayjs";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  ContactsOutlined,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import {
  Channel,
  ConversationListItem,
  conversationsApi,
} from "@/services/conversations.api";
import { ContactMatch, contactLevel, contactsApi } from "@/services/contacts.api";
import { codigoDeError, isCloudApi, threadIdFor, ventanaAbierta } from "@/services/channel-types";
import { SellerIcon } from "@/components";
import { NewContactCard } from "@/components/smart-select-contacts/forms/new-contact";
import { useContactConflictCheck } from "@/hooks/useContactConflictCheck";
import { axiosInstance, identityHydrationNotices } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
// Una restricción sin fecha de vencimiento también cuenta: ver services/channel-restriction.
import { estaRestringido } from "@/services/channel-restriction";

const { Text } = Typography;

/** #177: ¿la cuenta del canal está restringida *ahora*? Una fecha pasada ya no restringe. */

const restrictedMessage = (until?: string | null) =>
  `WhatsApp restringió este número hasta el ${dayjs(until).format("DD/MM/YYYY HH:mm")}. ` +
  `No se pueden iniciar conversaciones; re-vincular con QR no lo soluciona.`;

/**
 * Flujo "Nuevo mensaje" (Figma, sección **Contacts** — Groups y "Create new Contact" quedan
 * documentados para más adelante). Elegís canal + contacto → crea la conversación y la abre.
 *
 * Un canal de la Cloud API se ofrece también (change `conversaciones-templates`, D5, D14): a quien tiene
 * la ventana de 24 h abierta se le escribe como siempre, y a quien no, se le inicia con una plantilla
 * aprobada. El backend crea la conversación sólo si el contacto es una ficha de la tienda, y lo verifica
 * él; este modal sólo trabaja con fichas, así que un teléfono suelto nunca llega.
 *
 * Con canal y contacto ya elegidos —se viene de la ficha o la tabla de contactos— no se muestra: se
 * crea la conversación y se abre. Preguntar de nuevo por el contacto del que se venía no tenía sentido.
 */
export const NewMessageModal = ({
  open,
  onClose,
  storeCode,
  onCreated,
  initialChannelId,
  initialContact,
}: {
  open: boolean;
  onClose: () => void;
  storeCode: string;
  /** `viaTemplate`: la conversación no tiene ventana y el chat tiene que abrir el selector de plantillas. */
  onCreated: (conv: ConversationListItem, opts?: { viaTemplate?: boolean }) => void;
  initialChannelId?: string;
  initialContact?: ContactMatch;
}) => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelId, setChannelId] = useState<string>();
  const [q, setQ] = useState("");
  // Alta inline: el buscador dejaba sin salida cuando el contacto no existía (el "Sin contactos" era
  // terminal). Se reusa el form y la validación de identidad del alta de contactos del admin.
  const [formOpen, setFormOpen] = useState(false);
  const [form] = Form.useForm();
  const { runConflictCheck, applyConflictErrors, consumeBackendConflict } =
    useContactConflictCheck(form);
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<ContactMatch[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  /** Ya se respondió la lista de canales, haya venido o no el elegido. */
  const [cargados, setCargados] = useState(false);
  const directo = !!(initialContact && initialChannelId);
  const directoLanzado = useRef(false);

  useEffect(() => {
    if (!open || !storeCode) return;
    conversationsApi
      // Solo los canales donde el operador es Editor: por los demás no puede mandar nada, y
      // ofrecerlos en el desplegable era prometer algo que después falla.
      .listWritableChannels(storeCode)
      .then((all) => {
        setChannels(all);
        if (initialChannelId) {
          setChannelId(initialChannelId);
        } else {
          // #177: si hay un canal sano, arrancar con ese en vez de con uno restringido.
          const usable = all.find((c) => !estaRestringido(c)) ?? all[0];
          if (usable) setChannelId(usable.channelId);
        }
      })
      .catch(() => setChannels([]))
      .finally(() => setCargados(true));
  }, [open, storeCode, initialChannelId]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    // #137: no buscar sin store. Con storeCode vacío el API cae al fallback "todos los
    // stores permitidos" y el picker mostraría contactos de otros stores. El search queda
    // siempre scopeado al store del header.
    if (!debounced.trim() || !storeCode) {
      setResults([]);
      return;
    }
    let active = true;
    setSearching(true);
    contactsApi
      .searchContacts(debounced, storeCode)
      .then((r) => active && setResults(r))
      .finally(() => active && setSearching(false));
    return () => {
      active = false;
    };
  }, [debounced, storeCode]);

  const t = useTranslate();
  const selectedChannel = useMemo(
    () => channels.find((c) => c.channelId === channelId),
    [channels, channelId],
  );
  const channelType = selectedChannel?.type ?? "whatsapp";
  /**
   * #177: el canal elegido tiene la cuenta restringida por WhatsApp.
   *
   * Este flujo es justamente el que dispara la restricción —escribirle a alguien que no escribió
   * primero—, así que es donde más importa frenar. Crear la conversación igual no serviría: el
   * primer mensaje se rechaza y el operador se entera por el cliente.
   */
  const restricted = estaRestringido(selectedChannel);

  useEffect(() => {
    if (!open) {
      directoLanzado.current = false;
      setCargados(false);
      return;
    }
    if (!directo || !cargados || directoLanzado.current) return;
    directoLanzado.current = true;
    if (!selectedChannel) {
      message.error("Ese canal ya no está disponible para escribir desde este store");
      onClose();
      return;
    }
    // Si no se pudo, se vuelve a la bandeja con el motivo ya dicho: abrir el modal sería pedir de
    // nuevo un contacto que ya se eligió.
    void pick(initialContact!).then((ok) => !ok && onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, directo, cargados, selectedChannel]);

  /** `true` si la conversación quedó creada y abierta. */
  async function pick(contact: ContactMatch): Promise<boolean> {
    if (!channelId) {
      message.warning("Elegí un canal");
      return false;
    }
    if (!contact.whatsapp) {
      message.warning("El contacto no tiene WhatsApp cargado");
      return false;
    }
    if (restricted) {
      message.error(restrictedMessage(selectedChannel?.restrictedUntil));
      return false;
    }
    setCreating(true);
    try {
      /**
       * La persona puede venir **sin ficha en este store**: el search devuelve también a los
       * operadores del store y de sus sellers asociados, con `contactId: null`. El buscador de
       * operadores los ofrece igual porque para un cargo alcanza el `userId`; una conversación,
       * en cambio, se cuelga de la ficha. Así que se le crea acá, que es la misma decisión del
       * 007: al agregarlo se le crea la ficha en **este** store, no se reusa la de otro.
       */
      let contactId = contact.contactId ?? null;
      if (!contactId && contact.userId && storeCode) {
        contactId = await contactsApi.ensureContactInStore(contact.userId, storeCode);
        if (!contactId) {
          message.error("No se pudo crear la ficha del contacto en este store");
          return false;
        }
      }

      const cloud = isCloudApi(channelType);
      // El tipo decide la forma del hilo (D20): un JID en WhatsApp Web, los dígitos en la Cloud API. En la
      // Cloud API, `viaTemplate` crea la conversación aunque no haya ventana, para mandarle una plantilla.
      const conv = await conversationsApi.createConversation({
        channelId,
        externalThreadId: threadIdFor(channelType, contact.whatsapp),
        contactId: contactId ?? undefined,
        ...(cloud ? { viaTemplate: true } : {}),
      });
      const conVentana = !cloud || ventanaAbierta(channelType, conv.service_window_expires_at);
      // Sin aviso de "conversación abierta": se ve, el chat queda abierto. Y la falta de ventana ya la
      // dice el chat.
      reset();
      onCreated(conv, { viaTemplate: !conVentana });
      return true;
    } catch (e: any) {
      const codigo = codigoDeError(e);
      // Un backend sin plantillas no conoce `viaTemplate`, o responde con la ventana: no es un error del panel.
      if (codigo === "OUTSIDE_SERVICE_WINDOW" || (!codigo && /viaTemplate/.test(String(e?.response?.data?.message ?? "")))) {
        message.warning(`${contact.name} no escribió en las últimas 24 h, y este ambiente todavía no inicia con plantillas.`);
        return false;
      }
      if (codigo === "CONTACT_CHECK_UNAVAILABLE") {
        message.error("No se pudo verificar la ficha del contacto: la API principal no respondió. Probá de nuevo.");
        return false;
      }
      message.error(`No se pudo crear la conversación: ${e?.response?.data?.message ?? e?.message ?? e}`);
      return false;
    } finally {
      setCreating(false);
    }
  }

  function reset() {
    setQ("");
    setDebounced("");
    setResults([]);
    setFormOpen(false);
    form.resetFields();
  }

  /**
   * Da de alta el contacto y sigue con el flujo del modal: crear la conversación.
   *
   * Es el mismo alta del admin y **la misma llamada única** (OpenSpec 006 · D1): `POST /contacts`
   * resuelve la identidad —la reusa o la crea— y después crea la ficha. Antes acá eran dos
   * llamadas (`POST /users` y después `POST /contacts`), y eso costaba dos cosas:
   *
   * - **El nombre tipeado se perdía.** `POST /contacts` iba sin `firstname`/`lastname`, así que con
   *   el 024 —el nombre es por store, vive en `contact`— la ficha nacía sin nombre propio y el
   *   `COALESCE` caía al de la identidad: al dar de alta a alguien que ya existía en otro store, la
   *   conversación mostraba el nombre viejo en vez del que acababa de escribir el operador.
   * - **Identidades huérfanas.** Si la primera salía bien y la segunda fallaba, quedaba una cuenta
   *   que existe, ocupa el mail y nadie ve en el admin.
   */
  async function saveNewContact() {
    if (!channelId) {
      message.warning("Elegí un canal");
      return;
    }
    if (restricted) {
      message.error(restrictedMessage(selectedChannel?.restrictedUntil));
      return;
    }
    setCreating(true);
    try {
      const values = await form.validateFields();
      const { whatsappError, emailError } = await runConflictCheck(
        values.whatsapp,
        values.email,
        storeCode,
      );
      if (whatsappError || emailError) {
        applyConflictErrors(whatsappError, emailError);
        return;
      }

      const { data: created } = await axiosInstance.post(`${API_BASE_URL}/contacts`, {
        storeCode,
        // Datos de identidad: el backend resuelve si la reusa o la crea.
        email: values.email || undefined,
        whatsapp: values.whatsapp,
        // OpenSpec 024 — el nombre tipeado es de la FICHA de este store, y va acá.
        firstname: values.firstname,
        lastname: values.lastname,
        isActive: true,
      });
      const contactId = created?.contactId ?? created?.contact?.contactId;
      const userId = created?.userId ?? created?.contact?.userId;

      /**
       * D8 — si la persona ya existía, la ficha pudo quedar con el mail o el WhatsApp del
       * ecosistema en vez de con el que se tipeó. Se avisa acá y no sólo en Contactos: el operador
       * está por escribirle a ese número.
       */
      const notices = identityHydrationNotices(created, t);
      if (notices.hydrated) message.success(notices.hydrated);
      if (notices.discarded) message.warning(notices.discarded);

      // Recién creado, el contacto todavía no está en el buscador: se arma el match a mano y se
      // sigue derecho a abrir la conversación, que es para lo que el operador entró acá.
      //
      // El teléfono sale de la respuesta y no del formulario: si ganó el del ecosistema (D8), el
      // hilo tiene que abrirse contra **ese** número. Con el tipeado se le escribiría a otro.
      await pick({
        contactId: Number(contactId),
        userId,
        name: `${values.firstname} ${values.lastname}`.trim(),
        email: created?.identityEmail ?? values.email,
        whatsapp: created?.identityWhatsapp ?? values.whatsapp,
        storeCode,
      });
    } catch (e: any) {
      // Conflicto estructurado del backend (mismo teléfono/email en otra identidad) → al campo.
      if (consumeBackendConflict?.(e)) return;
      if (e?.errorFields) return; // validación del form: ya se muestra en los campos
      message.error(
        `No se pudo crear el contacto: ${e?.response?.data?.message ?? e?.message ?? e}`,
      );
    } finally {
      setCreating(false);
    }
  }

  return (
    <Modal
      title={t("conversations.newMessage.title")}
      open={open && !directo}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={null}
      width={450}
      styles={{ body: { height: 465, overflow: "visible" } }}
    >
      {!initialChannelId && (
        <Select
          style={{ width: "100%", marginBottom: 12 }}
          placeholder="Canal"
          value={channelId}
          onChange={setChannelId}
          // #177: los restringidos se muestran pero no se pueden elegir — esconderlos haría pensar
          // que el canal desapareció.
          options={channels.map((c) => ({
            value: c.channelId,
            label: `${c.name} · ${c.identifier}${estaRestringido(c) ? " · restringido" : ""}`,
            disabled: estaRestringido(c),
          }))}
        />
      )}

      {isCloudApi(channelType) && (
        <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 12 }}>
          Por WhatsApp Business, a quien escribió en las últimas 24 h se le responde como siempre; a quien no,
          se le inicia con una plantilla aprobada por Meta.
        </Text>
      )}

      {restricted && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="WhatsApp restringió este número"
          description={
            <>
              No se pueden iniciar conversaciones hasta el{" "}
              <strong>{dayjs(selectedChannel?.restrictedUntil).format("DD/MM/YYYY [a las] HH:mm")}</strong>.
              Elegí otro canal o esperá a que venza — re-vincular con QR no la levanta.
            </>
          }
        />
      )}

      {initialContact ? (
        <div
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 8,
            padding: "12px 16px",
            background: "#fafafa",
            display: "flex",
            alignItems: "center",
            gap: 12,
            cursor: creating ? "default" : "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { if (!creating) (e.currentTarget as HTMLElement).style.background = "#f0f9f4"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "#fafafa"; }}
          onClick={() => !creating && pick(initialContact)}
        >
          <div style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            {creating
              ? <Spin size="small" />
              : <ContactsOutlined style={{ fontSize: 16, color: "#8c8c8c" }} />
            }
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ display: "block", fontSize: 14 }}>{initialContact.name}</Text>
            <Text type="secondary" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              {channelType === "email"
                ? initialContact.email
                  ? <><MailOutlined /> {initialContact.email}</>
                  : "Sin email"
                : initialContact.whatsapp
                  ? <><WhatsAppOutlined /> {initialContact.whatsapp}</>
                  : "Sin WhatsApp"
              }
            </Text>
          </div>
        </div>
      ) : formOpen ? (
        <>
          <Form layout="vertical" form={form}>
            <NewContactCard disabled={creating} />
          </Form>
          <Flex justify="space-between" align="center" style={{ marginTop: 8 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={creating}
              onClick={() => setFormOpen(false)}
            >
              Atrás
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={creating}
              style={{ background: "#00733B" }}
              onClick={() => void saveNewContact()}
            >
              Guardar
            </Button>
          </Flex>
        </>
      ) : (
        <div style={{ position: "relative" }}>
          <Input
            allowClear
            autoFocus
            placeholder="Buscar por nombre, email o WhatsApp..."
            prefix={<SearchOutlined />}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          {(q.trim() || searching) && <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              background: "#fff",
              borderRadius: 10,
              boxShadow: "0 10px 26px rgba(0,0,0,0.14)",
              maxHeight: 360,
              overflowY: "auto",
              zIndex: 1050,
            }}
          >
          {searching || creating ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin />
            </div>
          ) : !debounced.trim() ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: "24px 0" }} description="Buscá un contacto por nombre, email o WhatsApp" />
          ) : results.filter((c) => channelType === "email" ? !!c.email : !!c.whatsapp).length === 0 ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <Text type="secondary">No se encontraron contactos</Text>
            </div>
          ) : (
            <List
              size="small"
              dataSource={results.filter((c) => channelType === "email" ? !!c.email : !!c.whatsapp)}
              renderItem={(c) => {
                // #111: label de nivel (Customer / Op. Store / Op. Seller) + ícono + store/seller.
                const lvl = contactLevel(c);
                const LvlIcon = lvl.kind === "seller" ? SellerIcon : lvl.kind === "store" ? ShopOutlined : UserOutlined;
                return (
                  <List.Item style={{ cursor: "pointer" }} onClick={() => pick(c)}>
                    <List.Item.Meta
                      title={<Text strong>{c.name}</Text>}
                      description={
                        <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 2 }}>
                          {channelType === "email" ? (
                            c.email
                              ? <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#8c8c8c" }}><MailOutlined /> {c.email}</span>
                              : <span style={{ color: "#8c8c8c" }}>Sin email</span>
                          ) : (
                            c.whatsapp
                              ? <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#8c8c8c" }}><WhatsAppOutlined /> {c.whatsapp}</span>
                              : <span style={{ color: "#8c8c8c" }}>Sin WhatsApp</span>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          )}
          {/* Pie del dropdown: la salida cuando el contacto no existe. */}
          <div style={{ borderTop: "1px solid #f0f0f0", padding: "4px 8px" }}>
            <Button
              type="link"
              icon={<PlusOutlined />}
              style={{ color: "#00733B", paddingLeft: 4 }}
              onClick={() => {
                const typed = q.trim();
                const digits = typed.replace(/\D/g, "");
                form.setFieldsValue(
                  digits.length >= 6 && digits.length >= typed.length - 4
                    ? { whatsapp: digits }
                    : { firstname: typed },
                );
                setFormOpen(true);
              }}
            >
              {t("button.createnewcontact")}
            </Button>
          </div>
          </div>}
        </div>
      )}
      {/* TODO (documentado): sección Groups (Figma). */}
    </Modal>
  );
};
